import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authcontext";
import { validateLogin } from "../utils/validators";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./login.css";

export default function Login() {
  const navigate = useNavigate();

  const { login } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] =
    useState(false);

  const [errors, setErrors] = useState({});

  const [serverError, setServerError] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  /* HANDLE INPUT CHANGE */

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });

    setErrors({
      ...errors,
      [e.target.name]: "",
    });

    setServerError("");
  };

  /* HANDLE LOGIN */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors =
      validateLogin(form);

    if (
      Object.keys(validationErrors)
        .length > 0
    ) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      await login({
        email: form.email.trim(),
        password: form.password,
      });

      navigate("/dashboard", {
        replace: true,
      });
    } catch (error) {
      const data = error.response?.data;
      let message = "Login failed. Please try again.";

      if (!error.response) {
        message = "Network error: Cannot connect to the server.";
      } else if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (data.message) {
          message = data.message;
        } else if (data.detail) {
          message = data.detail;
        } else if (typeof data === 'object') {
          // If object with field errors, format cleanly
          const entries = Object.entries(data);
          if (entries.length > 0) {
            message = entries
              .map(([field, errs]) => `${field !== 'non_field_errors' ? field + ': ' : ''}${Array.isArray(errs) ? errs.join(", ") : errs}`)
              .join(" | ");
          }
        }
      }

      setServerError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="split-login">
      {/* LEFT SIDE */}

      <div className="login-left">
        <div className="left-content">
          <h1>Syrano HRMS</h1>

          <p>
            Smart workforce management
            for
            <br />
            modern organizations.
          </p>

          <div className="illustration">
            <img
              src="/images/login.svg"
              alt="illustration"
            />
          </div>

         <div className="powered-by">
  <span>Powered by</span>
  <span className="powered-by-badge">
    <img src="/images/syran_logo2.png" alt="Syran Innovations logo" className="powered-by-logo" />
  </span>
  <strong>Syran Innovations</strong>
</div>
        </div>
      </div>

      {/* RIGHT SIDE */}

      <div className="login-right">
        <div className="login-right-inner">
          <div className="login-form-wrapper">
            <form
              className="login-form"
              onSubmit={handleSubmit}
            >
              <h2 className="login-heading">
                Welcome Back
              </h2>

              <p className="subtitle">
                Please sign in to your account
              </p>

              {/* SERVER ERROR */}

              {serverError && (
                <div className="error-box">
                  {serverError}
                </div>
              )}

              {/* EMAIL */}

              <div className="form-group">
                <label>
                  Username / Email
                </label>

                <input
                  type="text"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email ID"
                  autoComplete="off"
                />

                {errors.email && (
                  <span className="field-error">
                    {errors.email}
                  </span>
                )}
              </div>

              {/* PASSWORD */}

              <div className="form-group">
                <label>Password</label>

                <div className="password-input">
                  <input
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="password"
                    autoComplete="new-password"
                  />

                  <span
                    className="eye-icon"
                    onClick={() =>
                      setShowPassword(
                        !showPassword
                      )
                    }
                  >
                    {showPassword ? (
                      <FaEyeSlash />
                    ) : (
                      <FaEye />
                    )}
                  </span>
                </div>

                {errors.password && (
                  <span className="field-error">
                    {errors.password}
                  </span>
                )}
              </div>

              {/* FORGOT PASSWORD */}

              <div className="login-options">
                <span
                  className="forgot-link"
                  onClick={() =>
                    navigate(
                      "/forgot_password"
                    )
                  }
                >
                  Forgot Password?
                </span>
              </div>

              {/* LOGIN BUTTON */}

              <button
                type="submit"
                disabled={loading}
                className="login-btn"
              >
                {loading
                  ? "Signing in..."
                  : "Login"}
              </button>
            </form>
          </div>

          {/* FOOTER */}
          <footer className="login-footer">
            <div className="login-footer-divider" />
            <div className="login-footer-row">
              <span>© {new Date().getFullYear()} Syran Innovations. All rights reserved.</span>
              <span className="login-footer-sep">•</span>
              <a href="mailto:support@syrano.in">support@syrano.in</a>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}